import { Res } from 'core/api';
import { Log } from './Log';

export type ConnectionCheckListener = {
	onConnectionFailed: () => Promise<'retry' | 'abort'>;
	onCheckCompleted: () => void;
}

let currentCheck: Nullable<Promise<void>> = null;

export function ensureServerIsAlive(listener: ConnectionCheckListener): Promise<void> {
	// Si ya estoy en mitad de una comprobación, el resultado de esa me sirve para el resto de
	// procesos que quieran comprobar algo. Así además evito sacar varias veces el mensaje de
	// reintentar si hay algún error.
	if (currentCheck)
		return currentCheck;

	// Lanzo la comprobación y guardo una referencia a ella. Cuando se complete, limpio la
	// comprobación en curso.
	currentCheck = checkConnection(listener).then(() => {
		currentCheck = null;
	});

	return currentCheck;
}

async function checkConnection(listener: ConnectionCheckListener) {
	// Intento ver si el servidor está vivo. Para eso, lanzo una petición al endpoint /is-alive/ que
	// debería resolverse muy rápidamente, por lo que sólo doy SERVER_TIMEOUT_MS al servidor para que
	// responda, de forma que si la red está caída se notifique rápidamente en lugar de esperar el
	// timeout normal del fetch, que es muy largo (alrededor de 30s, dependiendo del browser).
	//
	// Si falla la conexión, durante AUTOMATIC_RETRIES_DURING_MS se realizan reintentos automáticos
	// sin intervención del usuario para ver si la red se levanta. Entre cada reintento se esperan
	// MS_BETWEEN_AUTOMATIC_RETRIES, de forma que si no hay red y el fetch falla muy rápido (antes de
	// que se cumpla SERVER_TIMEOUT_MS) no estemos en un bucle lanzando cientos de peticiones por
	// segundo.
	//
	// Una vez que pasa ese tiempo de reintentos automáticos, cuando se produce un fallo se notifica
	// al usuario a través de onConnectionFailed para que decida si quiere reintentar o abortar la
	// operación.

	const SERVER_TIMEOUT_MS = 1_500;
	const AUTOMATIC_RETRIES_DURING_MS = 3_000;
	const MS_BETWEEN_AUTOMATIC_RETRIES = 500;
	let msBetweenManualRetries = MS_BETWEEN_AUTOMATIC_RETRIES;

	const start = new Date().getTime();

	let connected = false;

	try {
		while (!connected) {
			try {
				const controller = new AbortController();

				const timeoutId = setTimeout(() => {
					controller.abort();
				}, SERVER_TIMEOUT_MS);

				Log.debug('is-alive: iniciando comprobación.');

				const response = await fetch('/is-alive/', { signal: controller.signal });
				// Aunque no queramos saber nada del resultado, forzamos la lectura de la respuesta para que se liberen
				// recursos y no generemos memory-leaks. Parece un mal diseño porque si la ignoras durante un tiempo el sistema debería liberarlo
				void response.text();

				clearTimeout(timeoutId);

				// Si se ha resuelto la promesa es que el servidor nos ha respondido algo
				// y nos ha enviado las cabeceras http (que es lo que hace que se resuelva
				// la primera promesa del fetch), por lo que consideramos que hay conexión.
				connected = true;

				Log.debug('is-alive: comprobación finalizada.');
			}
			catch (e: any) {
				if (e?.name !== 'AbortError') {
					Log.info(`is-alive: no ha sido posible comprobar la conexión con el servidor de Ágora: ${e}. Se reintentará la conexión.`);
				}

				const ellapsed = new Date().getTime() - start;
				if (ellapsed < AUTOMATIC_RETRIES_DURING_MS) {
					Log.debug('is-alive: realizando reintento automático.');
					await sleep(MS_BETWEEN_AUTOMATIC_RETRIES);
					continue;
				}

				const action = await listener.onConnectionFailed();
				if (action === 'abort') {
					Log.warn('is-alive: conexión abortada por el usuario.');
					throw new Error(Res.Msg.MSG_CANNOT_CONNECT_TO_SERVER_ABORT());
				}
				else {
					await sleep(msBetweenManualRetries);
					// Backoff para incrementar el delay hasta llegar a un máximo de 5s entre reintentos
					msBetweenManualRetries += MS_BETWEEN_AUTOMATIC_RETRIES;
					msBetweenManualRetries = Math.max(msBetweenManualRetries, 5_000);
				}
			}
		}
	} finally {
		listener.onCheckCompleted();
	}
}

function sleep(ms: number): Promise<void> {
	return new Promise(resolve => setTimeout(resolve, ms));
}

